/**
 * DevicePicker — 시뮬 대상 디바이스 선택 (포털 DeviceListPage 셸 대체 경량 버전).
 * 디바이스 목록을 불러와 카드 선택 → 부모가 DeviceDetailModal 오픈. device_code 직접 입력도 지원.
 * 개발/테스트용 "약달력 등록"(pair-bootstrap) + 등록 QR 도 여기서 제공(포털 디바이스 관리 미러).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button, Card, Empty, Form, Input, List, Modal, QRCode, Select, Space, Tag, Typography, message,
} from 'antd'
import { medApi, simExchange, toErrMsg, logout } from './sim/api'
import { useLabel } from './framework/useLabel'
import LocaleSelect from './framework/LocaleSelect'
import PrescriptionForm from './PrescriptionForm'
import { DISEASES } from './prescriptionTemplates'

const todayStr = () => new Date().toISOString().slice(0, 10)
const randIssueNo = () => Math.floor(1000 + Math.random() * 9000)

// 등록 QR — 포털과 동일한 페어링 랜딩 URL. device_code 자동 생성 = MAME- + 6 hex(대문자).
const PORTAL_ORIGIN = 'https://mame-portal.pages.dev'
const pairDeepLink = (code: string) => `${PORTAL_ORIGIN}/pair/${code}`
const genDeviceCode = () => {
  const b = new Uint8Array(3)
  crypto.getRandomValues(b)
  return 'MAME-' + Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('').toUpperCase()
}

export default function DevicePicker({ onOpen }: { onOpen: (code: string) => void }) {
  const { t } = useLabel()
  const [devices, setDevices] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [manual, setManual] = useState('')
  const [regOpen, setRegOpen] = useState(false)   // 등록 폼 모달
  const [regBusy, setRegBusy] = useState(false)
  const [qrCode, setQrCode] = useState<string | null>(null)  // 등록 성공 후 등록 QR 표시 대상
  const [regForm] = Form.useForm()
  // 처방전 발행(테스트) 모달
  const [presOpen, setPresOpen] = useState(false)
  const [presName, setPresName] = useState('')
  const [presDisease, setPresDisease] = useState(DISEASES[0].key)
  const [presIssueNo, setPresIssueNo] = useState(randIssueNo())
  const [presDate, setPresDate] = useState(todayStr())

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      setDevices((await medApi.devices.list()) ?? [])
    } catch (e) {
      message.error(t('SIM_MSG_DEVICE_LIST_FAIL', '디바이스 목록 로드 실패') + ': ' + toErrMsg(e))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { reload() }, [reload])

  const openRegister = () => {
    regForm.setFieldsValue({ device_code: genDeviceCode(), name: '', birth_date: '' })
    setRegOpen(true)
  }

  const submitRegister = async () => {
    try {
      const v = await regForm.validateFields()
      const code = (v.device_code || '').trim() || genDeviceCode()
      setRegBusy(true)
      await medApi.devices.pairBootstrap({
        device_code: code,
        patient: { name: v.name.trim(), birth_date: (v.birth_date || '').trim() || null },
        relation: 'FAMILY',
      })
      message.success(t('SIM_MSG_PILLCAL_REGISTERED', '약달력이 등록되었습니다'))
      setRegOpen(false)
      setQrCode(code)   // 등록 QR 표시
      reload()
    } catch (e: any) {
      if (e?.errorFields) return
      message.error(toErrMsg(e))
    } finally {
      setRegBusy(false)
    }
  }

  // 메인앱 계정으로 전환 — 클립보드(또는 붙여넣기)의 1회용 티켓으로 동일 사용자 토큰 교환 후 목록 갱신.
  const switchAccount = async () => {
    let ticket = ''
    try { ticket = (await navigator.clipboard.readText())?.trim() } catch { ticket = '' }
    if (!ticket) ticket = (window.prompt(t('SIM_PROMPT_PASTE_TICKET', '메인앱에서 복사한 시뮬 티켓을 붙여넣으세요')) ?? '').trim()
    if (!ticket) return
    try {
      await simExchange(ticket)
      message.success(t('SIM_MSG_ACCOUNT_SWITCHED', '메인앱 계정으로 전환되었습니다'))
      reload()
    } catch (e) {
      message.error(t('SIM_MSG_SWITCH_FAIL', '전환 실패') + ': ' + toErrMsg(e))
    }
  }

  // 처방전 발행(테스트) 열기 — 환자명 기본값 = 첫 디바이스의 환자명.
  const openPrescription = () => {
    const firstName = devices.find((d) => d.patient_name)?.patient_name ?? ''
    setPresName(firstName)
    setPresDisease(DISEASES[0].key)
    setPresIssueNo(randIssueNo())
    setPresDate(todayStr())
    setPresOpen(true)
  }

  // 등록된 환자 = 배정된 디바이스의 환자명(중복 제거). 처방전 환자 콤보 옵션.
  const patientNames = Array.from(
    new Set(devices.map((d) => d.patient_name).filter(Boolean)),
  ) as string[]

  // 등록 QR 공유 — 링크(Web Share/클립보드) / QR 이미지(canvas→Blob, Web Share files/다운로드).
  const qrBoxRef = useRef<HTMLDivElement>(null)
  const shareQrLink = async () => {
    if (!qrCode) return
    const url = pairDeepLink(qrCode)
    try {
      if (navigator.share) await navigator.share({ title: t('SIM_TTL_REGISTER_QR', '등록 QR'), text: qrCode, url })
      else { await navigator.clipboard.writeText(url); message.success(t('SIM_MSG_LINK_COPIED', '링크가 복사되었습니다')) }
    } catch { /* 사용자 취소/미지원 — 무시 */ }
  }
  const shareQrImage = async () => {
    const canvas = qrBoxRef.current?.querySelector('canvas')
    if (!canvas || !qrCode) return
    canvas.toBlob(async (blob) => {
      if (!blob) return
      const file = new File([blob], `qr-${qrCode}.png`, { type: 'image/png' })
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: qrCode })
        } else {
          const a = document.createElement('a')
          a.href = URL.createObjectURL(blob); a.download = `qr-${qrCode}.png`; a.click(); URL.revokeObjectURL(a.href)
          message.success(t('SIM_MSG_QR_SAVED', 'QR 이미지를 저장했습니다'))
        }
      } catch { /* 취소 무시 */ }
    })
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <Typography.Title level={4} style={{ margin: 0, flex: 1, color: '#6750a4' }}>💊 Mame</Typography.Title>
        <LocaleSelect size="small" />
        <Button onClick={switchAccount}>{t('SIM_SWITCH_ACCOUNT', '메인앱 계정으로 전환')}</Button>
        <Button onClick={openPrescription}>{t('SIM_BTN_ISSUE_PRESC', '처방전 발행(테스트)')}</Button>
        <Button type="primary" onClick={openRegister}>{t('SIM_BTN_REGISTER_PILLCAL', '새 약달력 등록')}</Button>
        <Button onClick={reload} loading={loading}>{t('SIM_BTN_REFRESH', '새로고침')}</Button>
        <Button onClick={() => { logout(); location.reload() }}>{t('SIM_BTN_LOGOUT', '로그아웃')}</Button>
      </div>

      <Card size="small" style={{ marginBottom: 12, borderRadius: 12 }}>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder={t('SIM_PH_DEVICE_CODE', 'device_code 직접 입력 (예: MAME-XXXX)')}
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onPressEnter={() => manual.trim() && onOpen(manual.trim())}
          />
          <Button type="primary" disabled={!manual.trim()} onClick={() => onOpen(manual.trim())}>{t('SIM_BTN_OPEN', '열기')}</Button>
        </Space.Compact>
      </Card>

      {devices.length === 0 && !loading ? (
        <Empty description={t('SIM_EMPTY_NO_DEVICE', '디바이스 없음')} />
      ) : (
        <List
          loading={loading}
          dataSource={devices}
          renderItem={(d: any) => {
            const code = d.device_code ?? d.code
            // 미배정(환자 미연결) → 등록 QR 버튼 노출(스캔해 페어링). 배정됨 → 환자명 태그만.
            const actions = [<Button key="o" type="primary" onClick={() => onOpen(code)}>{t('SIM_BTN_OPEN_SIM', '시뮬 열기')}</Button>]
            if (!d.patient_name) actions.unshift(<Button key="qr" onClick={() => setQrCode(code)}>{t('SIM_SHOW_QR', '등록 QR')}</Button>)
            return (
              <List.Item
                actions={actions}
                style={{ background: '#fff', borderRadius: 12, marginBottom: 8, padding: '10px 14px' }}
              >
                <List.Item.Meta
                  title={<span style={{ color: '#1f2937' }}>{code}</span>}
                  description={
                    <Space size={6} wrap>
                      {d.patient_name && <Tag color="blue">{d.patient_name}</Tag>}
                      {d.location && <span style={{ color: '#6b7280' }}>{d.location}</span>}
                      {d.status && <Tag color={d.status === 'ACTIVE' ? 'green' : 'orange'}>{d.status}</Tag>}
                      {d.sim_allowed === 0 && <Tag color="red">{t('SIM_TAG_SIM_DISABLED', 'sim 불가')}</Tag>}
                    </Space>
                  }
                />
              </List.Item>
            )
          }}
        />
      )}

      {/* 약달력 등록(테스트) — pair-bootstrap: device 자동생성 + 환자 + 바인딩 */}
      <Modal
        title={t('SIM_TTL_REGISTER_PILLCAL', '약달력 등록 (테스트)')}
        open={regOpen}
        onCancel={() => setRegOpen(false)}
        onOk={submitRegister}
        okText={t('SIM_BTN_REGISTER', '등록')}
        cancelText={t('SIM_BTN_CANCEL', '취소')}
        confirmLoading={regBusy}
      >
        <Form form={regForm} layout="vertical">
          <Form.Item name="device_code" label={t('SIM_LBL_PILLCAL_CODE', '약달력 코드')}
            extra={t('SIM_EXTRA_AUTO_CODE', '비워두면 자동 생성(MAME-XXXXXX)됩니다.')}>
            <Input placeholder="MAME-XXXXXX" />
          </Form.Item>
          <Form.Item name="name" label={t('SIM_LBL_PATIENT_NAME', '환자 이름')} rules={[{ required: true, message: t('SIM_RULE_PATIENT_NAME', '환자 이름을 입력하세요') }]}>
            <Input placeholder={t('SIM_PH_PATIENT_NAME', '예: 홍길동')} />
          </Form.Item>
          <Form.Item name="birth_date" label={t('SIM_LBL_BIRTH_DATE', '생년월일 (YYYY-MM-DD, 선택)')}>
            <Input placeholder="1945-03-15" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 등록 QR — 포털 "등록 QR" 미러. 페어링 랜딩 URL(/pair/{code}). */}
      <Modal
        title={`${t('SIM_TTL_REGISTER_QR', '등록 QR')} — ${qrCode ?? ''}`}
        open={!!qrCode}
        onCancel={() => setQrCode(null)}
        footer={<Button onClick={() => setQrCode(null)}>{t('SIM_BTN_CLOSE', '닫기')}</Button>}
        width={340}
      >
        <div ref={qrBoxRef} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          {qrCode && <QRCode value={pairDeepLink(qrCode)} size={220} />}
          <Typography.Text code copyable>{qrCode}</Typography.Text>
          {/* 등록 QR 공유 — 링크 / QR 이미지 */}
          <Space>
            <Button onClick={shareQrLink}>{t('SIM_SHARE_LINK', '링크 공유')}</Button>
            <Button onClick={shareQrImage}>{t('SIM_SHARE_QR', 'QR 이미지 공유')}</Button>
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
            {t('SIM_HINT_REGISTER_QR', '이 QR을 다른 폰으로 스캔하면 약달력 연결(페어링) 화면으로 이동합니다. 인쇄해 기기에 부착할 수도 있습니다.')}
          </Typography.Text>
        </div>
      </Modal>

      {/* 처방전 발행(테스트) — 환자+병명 선택 → 별지 제10호 처방전 렌더. 화면을 폰으로 촬영해 모바일 OCR 테스트. */}
      <Modal
        title={t('SIM_TTL_ISSUE_PRESC', '처방전 발행 (테스트)')}
        open={presOpen}
        onCancel={() => setPresOpen(false)}
        footer={<Button onClick={() => setPresOpen(false)}>{t('SIM_BTN_CLOSE', '닫기')}</Button>}
        width={720}
      >
        <Space style={{ marginBottom: 12 }} wrap>
          {/* 환자명 = 등록된 환자 콤보 선택(텍스트 입력 → 콤보). 등록 환자 없으면 안내. */}
          <Select
            style={{ width: 160 }}
            value={presName || undefined}
            onChange={setPresName}
            placeholder={t('SIM_PH_PATIENT_FULLNAME', '환자 성명')}
            options={patientNames.map((n) => ({ value: n, label: n }))}
            notFoundContent={t('SIM_NO_PATIENT', '등록된 환자 없음')}
          />
          <Select
            style={{ width: 140 }}
            value={presDisease}
            onChange={setPresDisease}
            options={DISEASES.map((d) => ({ value: d.key, label: `${d.key} (${d.code})` }))}
          />
          <Button onClick={() => { setPresIssueNo(randIssueNo()); setPresDate(todayStr()) }}>{t('SIM_BTN_NEW_ISSUE_NO', '새 처방번호')}</Button>
        </Space>
        <PrescriptionForm
          patientName={presName}
          disease={DISEASES.find((d) => d.key === presDisease) ?? DISEASES[0]}
          issuedDate={presDate}
          issueNo={presIssueNo}
        />
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          {t('SIM_HINT_PRESC_OCR', '이 처방전을 다른 기기(폰)의 모바일 앱 "처방전" 탭에서 촬영하면 OCR→복용일정 등록을 테스트할 수 있습니다.')}
        </Typography.Text>
      </Modal>
    </div>
  )
}
